/**
 * Screen-edge geometry for urgent alerts (pure, no React/Three): how strongly
 * each edge glows for an event, and where an arrow pointing at an off-screen
 * event sits on the edge. Positions come in normalised device coordinates
 * (x right, y up, the view spanning -1…1).
 */

export interface EdgeWeights {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** How much every edge glows, whatever the side: the event is felt all round. */
const BASE_GLOW = 0.3;

/**
 * Glow per edge for an event at `ndc`: every edge a little, the edges on the
 * event's side (relative to the view's centre) up to full; on screen near
 * the centre, all edges alike.
 */
export function edgeWeights(x: number, y: number): EdgeWeights {
  // Beyond the view, only the direction matters.
  const len = Math.max(1, Math.abs(x), Math.abs(y));
  const dx = x / len;
  const dy = y / len;
  const side = (v: number) => BASE_GLOW + (1 - BASE_GLOW) * Math.max(0, v);
  return { top: side(dy), right: side(dx), bottom: side(-dy), left: side(-dx) };
}

/** Pixels kept clear at each edge (the HUD's columns and bars). */
export interface EdgeInset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface EdgePoint {
  /** Whether the event is in view (inside the inset area): no arrow then. */
  onScreen: boolean;
  /** Arrow position in px from the view's top-left. */
  x: number;
  y: number;
  /** Arrow direction in radians, screen space (0 = right, π/2 = down). */
  angle: number;
}

/**
 * Where the arrow for an event at `ndc` goes in a `width` × `height` view:
 * on the border of the area inside `inset`, along the line from that area's
 * centre towards the event.
 */
export function edgePoint(
  x: number,
  y: number,
  width: number,
  height: number,
  inset: EdgeInset
): EdgePoint {
  const px = ((x + 1) / 2) * width;
  const py = ((1 - y) / 2) * height;
  const left = Math.min(inset.left, width / 2);
  const right = Math.max(width - inset.right, width / 2);
  const top = Math.min(inset.top, height / 2);
  const bottom = Math.max(height - inset.bottom, height / 2);
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;
  const dx = px - cx;
  const dy = py - cy;
  const angle = Math.atan2(dy, dx);
  if (px >= left && px <= right && py >= top && py <= bottom) {
    return { onScreen: true, x: px, y: py, angle };
  }
  const hw = (right - left) / 2;
  const hh = (bottom - top) / 2;
  const t = Math.min(
    dx === 0 ? Number.POSITIVE_INFINITY : hw / Math.abs(dx),
    dy === 0 ? Number.POSITIVE_INFINITY : hh / Math.abs(dy)
  );
  return { onScreen: false, x: cx + dx * t, y: cy + dy * t, angle };
}
